import { cx } from '@twind/core';
import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import useIndicationSelector from 'app/hooks/useIndicationSelector';
import useRxConditionId from 'app/hooks/useRxConditionId';
import useShouldAutoOpen from 'app/modules/tailor/hooks/useShouldAutoOpen';
import { stripHTML } from 'app/utils/stripHTML';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ChatVersion } from 'app/copied/constants/common';

import DropdownMenu, { DropdownMenuItemInterface, DropdownMenuItemProps } from './DropdownMenu';
import OstroDynamicContent from './OstroDynamicContent';
import RightChevronIcon from './icons/RightChevronIcon';

const IndicationSelectorMenuItem = ({
  item,
  onItemSelect,
  className,
  underlineColor,
  style,
  isActive,
}: DropdownMenuItemProps) => {
  return (
    <li
      className={cx(
        '~(group flex items-start justify-between px-4 py-3 cursor-pointer text-gray-700)',
        className,
        'ostro-tailor-indication-selector-menu-item'
      )}
      onClick={() => onItemSelect?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onItemSelect?.(item);
        }
      }}
      tabIndex={onItemSelect ? 0 : -1}
    >
      <div className="w-full text-left">
        <OstroDynamicContent
          style={{
            textDecorationColor: underlineColor,
            ...style,
          }}
          className={cx(
            '~(text-sm font-semibold group-hover:underline decoration-[1.75px] [&_p]:mb-1)',
            isActive ? 'underline' : ''
          )}
          content={item.name}
        />
        <OstroDynamicContent
          className="text-xs [&_p]:mb-0"
          content={item.description}
        />
      </div>
    </li>
  );
};

interface PrimaryIndicationSelectorProps {
  className?: string;
  containerRef?: React.RefObject<HTMLElement>;
  useCustomButton?: boolean;
}

interface UnityButtonProps {
  title?: string;
  className?: string;
  isOpen: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  selectedItem?: DropdownMenuItemInterface;
}

export function UnityPrimaryIndicationSelectorButton({
  title,
  className,
  isOpen,
  onClick,
  selectedItem,
}: UnityButtonProps) {
  return (
    <button
      type="button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      onClick={onClick}
      className={cx('~(w-full flex items-center gap-2 px-4 py-3)', className)}
    >
      <div className="flex items-center gap-3">
        <OstroDynamicContent
          className={cx(
            `[&_p]:mb-0 text-[20px] font-bold border-b border-b-[2px] border-b-[${selectedItem?.underlineColor}]`
          )}
          content={selectedItem?.displayName || title}
        />
      </div>
      <RightChevronIcon className="w-3 h-3 text-black" />
    </button>
  );
}

const PrimaryIndicationSelector = ({
  className,
  containerRef,
  useCustomButton = false,
}: PrimaryIndicationSelectorProps) => {
  const [conditionId] = useRxConditionId();
  const { indicationSelectorModule, primaryIndicationSelectorEnabled, getSelectedIndication } = useIndicationSelector();
  const { captureEventWithActiveScreen } = useAnalytics();
  const [, setShouldAutoOpen] = useShouldAutoOpen();
  const webchatVersion = useWebchatVersion();
  const isV41 = webchatVersion === ChatVersion.v4_1;

  const handleOpen = () => {
    captureEventWithActiveScreen(PosthogEventName.IndicationMenuOpened);
  };
  const handleItemSelect = (item: DropdownMenuItemInterface) => {
    captureEventWithActiveScreen(PosthogEventName.IndicationChanged, {
      indication_selector_text: stripHTML(item.name),
      selectorLocation: 'Primary',
      currentConditionId: conditionId,
      newConditionId: item.id,
    });
    window?.TAIlor?.reloadWithId(item.id);
    setShouldAutoOpen(true);
  };
  if (!primaryIndicationSelectorEnabled) return null;

  const selectedIndication = getSelectedIndication();

  return (
    <DropdownMenu
      buttonComponent={useCustomButton ? UnityPrimaryIndicationSelectorButton : undefined}
      containerRef={containerRef}
      selectedItem={{
        name: selectedIndication?.indication_name ?? '',
        description: selectedIndication?.description ?? '',
        id: selectedIndication?.hub_condition_uuid ?? '',
        displayName: selectedIndication?.display_name ?? '',
        underlineColor: selectedIndication?.border_color,
      }}
      id="ostro-tailor-primary-indication-selector"
      title={indicationSelectorModule?.header_title ?? ''}
      items={
        indicationSelectorModule?.items?.map((item) => ({
          name: item.indication_name ?? '',
          description: item.description ?? '',
          id: item.hub_condition_uuid ?? '',
          displayName: item.display_name ?? '',
          underlineColor: item.border_color,
          style: {
            ...(conditionId === item.hub_condition_uuid ? { textDecoration: 'underline' } : {}),
          },
        })) ?? []
      }
      onItemSelect={handleItemSelect}
      className={className}
      disclaimer={indicationSelectorModule?.disclaimer_text}
      disclaimerEnabled={!!indicationSelectorModule?.disclaimer_enabled}
      menuItemComponent={IndicationSelectorMenuItem}
      onOpen={handleOpen}
      buttonClassName={
        !useCustomButton
          ? cx(
              'px-3 py-2',
              `${
                selectedIndication
                  ? !isV41
                    ? `border-[${selectedIndication.border_size}px] border-[${selectedIndication.border_color}] bg-[${selectedIndication.background_color}] rounded-[${selectedIndication.border_radius}px]`
                    : `font-semibold border-none focus:(ring-0) [&_div]:border-b-2 [&_div]:border-b-[${selectedIndication.border_color}]`
                  : ''
              }`
            )
          : undefined
      }
    />
  );
};

const SecondaryIndicationSelector = ({ className }: { className?: string }) => {
  const [conditionId] = useRxConditionId();
  const { secondaryIndicationSelectorEnabled, indicationSelectorModule } = useIndicationSelector();
  const { captureEventWithActiveScreen } = useAnalytics();
  const [, setShouldAutoOpen] = useShouldAutoOpen();
  const handleItemSelect = (item: DropdownMenuItemInterface) => {
    captureEventWithActiveScreen(PosthogEventName.IndicationChanged, {
      indication_selector_text: stripHTML(item.name),
      selectorLocation: 'Secondary',
    });
    window?.TAIlor?.reloadWithId(item.id);
    setShouldAutoOpen(true);
  };

  if (!secondaryIndicationSelectorEnabled) return null;

  return (
    <div
      id="ostro-tailor-secondary-indication-selector"
      className={className}
    >
      <OstroDynamicContent content={indicationSelectorModule?.header_title} />
      <ul>
        {indicationSelectorModule?.items?.map((item) => (
          <IndicationSelectorMenuItem
            key={item.hub_condition_uuid}
            item={{
              name: item.indication_name ?? '',
              description: item.description ?? '',
              id: item.hub_condition_uuid ?? '',
            }}
            onItemSelect={handleItemSelect}
            className="px-4 pl-0"
            underlineColor={item.border_color}
            isActive={conditionId === item.hub_condition_uuid}
          />
        ))}
      </ul>
      {indicationSelectorModule?.disclaimer_enabled && indicationSelectorModule?.disclaimer_text && (
        <IndicationSelectorMenuItem
          item={{ name: '', description: indicationSelectorModule?.disclaimer_text, id: '' }}
          onItemSelect={() => {}}
          className="px-4 pl-0 cursor-default"
        />
      )}
    </div>
  );
};

export { PrimaryIndicationSelector, SecondaryIndicationSelector };
